import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "../lib/api";
import { HardwireLogo } from "../components/HardwireMark";
import { RequestReceipt } from "../components/RequestReceipt";
import { CATEGORY_PHOTOS } from "../lib/photos";
import { toast } from "sonner";
import {
  Phone, ArrowRight, ArrowLeft, Check, Upload, X, Sparkles, Send, Loader2,
  Settings, PlugZap, Power, Lightbulb, Wifi, Wrench, Home as HomeIcon, Building2,
  CheckCircle2, MessageSquare,
} from "lucide-react";

const CAT_IMG = CATEGORY_PHOTOS;
const ICONS = { settings: Settings, plug: PlugZap, power: Power, lightbulb: Lightbulb, wifi: Wifi, wrench: Wrench, home: HomeIcon, building: Building2 };
const STEPS = ["Project Type", "Project Details", "Photos & Documents", "Location", "Review & Submit"];
const STEP_HEAD = [
  { t: "What do you need help with?", s: "Select the option that best fits your project." },
  { t: "Tell us about your project", s: "Add any options and a short description." },
  { t: "Photos & documents", s: "Optional — photos help us estimate accurately." },
  { t: "Your location & contact", s: "Where is the work, and how do we reach you?" },
  { t: "Review & submit", s: "Confirm your details and preliminary estimate." },
];

export default function SmartQuote() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(null);
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", town: params.get("town") || "", description: "", preferred_date: "", preferred_window: "Morning (8–12)" });
  const [photos, setPhotos] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [advOpen, setAdvOpen] = useState(false);
  const [advQ, setAdvQ] = useState("");
  const [advMsgs, setAdvMsgs] = useState([]);
  const [advLoading, setAdvLoading] = useState(false);

  useEffect(() => { api.get("/quote/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const cat = cats.find((c) => c.id === category);
  const custom = cat && cat.base_low === 0 && cat.base_high === 0;
  const est = (() => {
    if (!cat) return { low: 0, high: 0 };
    let low = cat.base_low, high = cat.base_high;
    (cat.options || []).forEach((o) => { if (options.includes(o.id)) { low += o.add_low; high += o.add_high; } });
    return { low, high };
  })();

  const toggleOpt = (id) => setOptions((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  const onPhotos = (e) => {
    Array.from(e.target.files).slice(0, 4 - photos.length).forEach((f) => {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type) || f.size > 100 * 1024) { toast.error('Use JPG, PNG or WebP photos up to 100 KB here. Larger photos and PDFs can be uploaded securely in your account.'); return; }
      const r = new FileReader(); r.onload = () => setPhotos((p) => [...p, r.result]); r.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const next = () => {
    if (step === 0 && !category) { toast.error("Please pick a project type."); return; }
    if (step === 3 && (!form.name.trim() || !form.email.includes("@"))) { toast.error("Name and a valid email are required."); return; }
    if (step < 4) setStep(step + 1); else submit();
  };

  const submit = async () => {
    if (!consent) { toast.error('Please acknowledge the privacy notice before submitting.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quote/submit", { category, options, photos, consent, ...form });
      setDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const askAdvisor = async () => {
    if (!advQ.trim()) return;
    const q = advQ; setAdvQ("");
    const history = advMsgs.map((m) => ({ role: m.role, content: m.text }));
    setAdvMsgs((m) => [...m, { role: "user", text: q }]); setAdvLoading(true);
    try { const { data } = await api.post("/quote/advisor", { question: q, history }); setAdvMsgs((m) => [...m, { role: "assistant", text: data.answer }]); }
    catch { setAdvMsgs((m) => [...m, { role: "assistant", text: "Advisor is unavailable right now." }]); }
    finally { setAdvLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-2.5">
          <button onClick={() => navigate('/')} data-testid="quote-logo-home" aria-label="Hardwire home"><HardwireLogo size={34} /></button>
          <nav className="hidden items-center gap-6 md:flex">
            {["Home", "Services", "Projects", "Resources", "Contact"].map((n) => (
              <button key={n} onClick={() => navigate(n === 'Home' ? '/' : `/${n.toLowerCase()}`)} data-testid={`quote-nav-${n.toLowerCase()}`} className="text-sm font-medium text-slate-600 hover:text-[#1286F2]">{n}</button>
            ))}
          </nav>
          <a href="tel:3526153878" className="flex items-center gap-2 rounded-full bg-[#1286F2] px-4 py-2 text-sm font-bold text-white" data-testid="quote-call"><Phone size={15} /> 352-615-3878</a>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8">
        {done ? (
          <div className="mx-auto max-w-lg rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid="quote-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><CheckCircle2 size={34} /></div>
            <h1 className="mt-5 font-display text-3xl font-black uppercase tracking-tight">Request received</h1>
            <p className="mt-2 text-slate-600">Thanks, {form.name.split(" ")[0]}! A licensed Hardwire electrician will review your project and confirm final pricing.</p>
            <div className="mx-auto mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-[#1286F2]">Preliminary range</div>
              <div className="mt-1 font-display text-4xl font-black">{done.preliminary_low === 0 && done.preliminary_high === 0 ? "Custom" : `${money(done.preliminary_low)}–${money(done.preliminary_high)}`}</div>
              <div className="mt-2 text-xs text-slate-500">Non-binding, pending professional review.</div>
            </div>
            <RequestReceipt result={done} email={form.email} />
            <button onClick={() => navigate("/")} className="mt-8 rounded-xl bg-[#1286F2] px-8 py-3 font-bold text-white hover:bg-[#0A84FF]" data-testid="quote-home">Back to Home</button>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
            {/* Sidebar */}
            <aside className="rounded-3xl bg-[#0B1120] p-6 text-white">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#38BDF8]">Get a Quote</div>
              <h1 className="mt-2 font-display text-3xl font-black leading-tight">Let's get your project started.</h1>
              <p className="mt-2 text-sm text-slate-400">Answer a few quick questions and we'll provide a detailed estimate.</p>
              <div className="mt-6 space-y-1">
                {STEPS.map((s, i) => (
                  <button key={s} onClick={() => i < step && setStep(i)} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${i === step ? "bg-[#1286F2]/15 ring-1 ring-[#1286F2]/40" : i < step ? "hover:bg-white/5" : "opacity-50"}`} data-testid={`step-nav-${i}`}>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-500 text-slate-900" : i === step ? "bg-[#1286F2] text-white" : "border border-slate-600 text-slate-400"}`}>{i < step ? <Check size={14} /> : i + 1}</span>
                    <span>
                      <span className={`block text-sm font-semibold ${i === step ? "text-[#38BDF8]" : "text-white"}`}>{s}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-700 p-3">
                <button onClick={() => setAdvOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1286F2] text-white" data-testid="sidebar-advisor"><MessageSquare size={18} /></button>
                <div className="text-xs">
                  <div className="font-semibold text-white">Need Help?</div>
                  <div className="text-slate-400">Chat with our team<br />or call <a href="tel:3526153878" className="text-[#38BDF8]">352-615-3878</a></div>
                </div>
              </div>
            </aside>

            {/* Content */}
            <main className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
              <div className="mb-5">
                <div className="flex items-center justify-between text-sm font-semibold text-slate-500">
                  <span>Step {step + 1} of 5</span><span>{(step + 1) * 20}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-[#1286F2] transition-all" style={{ width: `${(step + 1) * 20}%` }} /></div>
              </div>
              <h2 className="font-display text-3xl font-black tracking-tight">{STEP_HEAD[step].t}</h2>
              <p className="mt-1 text-slate-500">{STEP_HEAD[step].s}</p>

              <div className="mt-6">
                {step === 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="step-category">
                    {cats.map((c) => {
                      const active = category === c.id; const Ic = ICONS[c.icon] || Wrench; const img = CAT_IMG[c.id];
                      return (
                        <button key={c.id} onClick={() => { setCategory(c.id); setOptions([]); }} className={`overflow-hidden rounded-2xl border text-left transition ${active ? "border-[#1286F2] ring-2 ring-[#1286F2]" : "border-slate-200 hover:border-slate-300"}`} data-testid={`cat-${c.id}`}>
                          <div className="flex h-28 items-center justify-center bg-slate-100">
                            {img ? <img src={img} alt={c.name} loading="lazy" className="h-full w-full object-cover" /> : <Ic size={40} className="text-[#1286F2]" />}
                          </div>
                          <div className="p-3 text-sm font-semibold leading-tight">{c.name}</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {step === 1 && cat && (
                  <div data-testid="step-details">
                    {(cat.options || []).length > 0 && (
                      <div className="mb-5 space-y-2">
                        <div className="text-sm font-medium text-slate-600">Options (optional)</div>
                        {cat.options.map((o) => (
                          <label key={o.id} className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 p-3" data-testid={`opt-${o.id}`}>
                            <span className="flex items-center gap-3"><input type="checkbox" checked={options.includes(o.id)} onChange={() => toggleOpt(o.id)} className="h-5 w-5 accent-[#1286F2]" /><span className="font-medium">{o.label}</span></span>
                            <span className="text-sm text-slate-500">+{money(o.add_low)}–{money(o.add_high)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <textarea className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" rows={5} placeholder="Describe your project (what, where, any issues)…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="quote-description" />
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="step-photos">
                    <div className="flex flex-wrap gap-3">
                      {photos.map((p, i) => (
                        <div key={i} className="relative h-24 w-24 overflow-hidden rounded-xl border border-slate-200">
                          <img src={p} alt="" className="h-full w-full object-cover" />
                          <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X size={12} /></button>
                        </div>
                      ))}
                      {photos.length < 4 && (
                        <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#1286F2]" data-testid="photo-upload">
                          <Upload size={20} /><span className="text-[10px]">Add photo</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                        </label>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-slate-500">Up to 4 JPG, PNG or WebP photos, 100 KB each. For larger photos or PDF plans, use your private customer or builder account. This step is optional.</p>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-3" data-testid="step-location">
                    <input className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name" />
                    <input className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email" />
                    <input className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone" />
                    <input className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Service address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="quote-address" />
                    <input className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Town / city" maxLength={80} value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} data-testid="quote-town" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="date" className="rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="quote-date" />
                      <select className="rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" value={form.preferred_window} onChange={(e) => setForm({ ...form, preferred_window: e.target.value })} data-testid="quote-window"><option>Morning (8–12)</option><option>Afternoon (12–5)</option><option>Flexible</option></select>
                    </div>
                  </div>
                )}

                {step === 4 && cat && (
                  <div data-testid="step-review">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#1286F2]">Preliminary estimate</div>
                      <div className="mt-1 font-display text-4xl font-black">{custom ? "Custom — we'll price it" : `${money(est.low)}–${money(est.high)}`}</div>
                      <div className="mt-1 text-xs text-slate-500">Non-binding. A licensed Hardwire electrician verifies scope, code & final pricing on site.</div>
                      <div className="mt-4 space-y-1 text-sm text-slate-600">
                        <div><b className="text-slate-800">Project:</b> {cat.name}</div>
                        {options.length > 0 && <div><b className="text-slate-800">Options:</b> {cat.options.filter((o) => options.includes(o.id)).map((o) => o.label).join(", ")}</div>}
                        {form.description && <div><b className="text-slate-800">Notes:</b> {form.description}</div>}
                        <div><b className="text-slate-800">Contact:</b> {form.name} · {form.email} {form.phone && `· ${form.phone}`}</div>
                        {form.address && <div><b className="text-slate-800">Address:</b> {form.address}</div>}
                        {form.town && <div data-testid="quote-review-town"><b className="text-slate-800">Town:</b> {form.town}</div>}
                        {form.preferred_date && <div><b className="text-slate-800">Preferred:</b> {form.preferred_date} · {form.preferred_window}</div>}
                      </div>
                    </div>
                    <label className="mt-5 flex items-start gap-3 text-sm text-slate-600"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} data-testid="quote-consent" className="mt-1" /><span>I agree to be contacted about this request and acknowledge the <a href="/privacy" target="_blank" rel="noreferrer" className="underline" data-testid="quote-privacy">Privacy Policy</a>. This request is not a confirmed appointment.</span></label>
                  </div>
                )}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="flex items-center gap-1 rounded-xl border border-slate-300 px-5 py-3 font-semibold text-slate-600 disabled:opacity-30" data-testid="quote-back"><ArrowLeft size={16} /> Back</button>
                <button onClick={next} disabled={submitting} className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-7 py-3 font-bold text-white hover:bg-[#0A84FF] disabled:opacity-50" data-testid="quote-next">
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : step === 4 ? <>Submit <Check size={16} /></> : <>Next <ArrowRight size={16} /></>}
                </button>
              </div>
            </main>
          </div>
        )}
      </div>

      {advOpen && (
        <div className="fixed bottom-5 right-5 z-50 flex h-[460px] w-[340px] flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl" data-testid="advisor-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2"><Sparkles size={16} className="text-[#1286F2]" /><span className="font-bold">Project Advisor</span></div>
            <button onClick={() => setAdvOpen(false)}><X size={18} className="text-slate-400" /></button>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto p-3">
            <div className="rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-700">Hi! Tell me about your project and I'll help you figure out what you need. (General guidance — a licensed electrician confirms final scope.)</div>
            {advMsgs.map((m, i) => <div key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-[#1286F2] text-white" : "bg-slate-100 text-slate-700"}`}>{m.text}</div>)}
            {advLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="animate-spin" size={14} /> Thinking…</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); askAdvisor(); }} className="flex gap-2 border-t border-slate-100 p-3">
            <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1286F2] focus:outline-none" placeholder="Ask about your project…" value={advQ} onChange={(e) => setAdvQ(e.target.value)} data-testid="advisor-input" />
            <button className="rounded-lg bg-[#1286F2] px-3 text-white" data-testid="advisor-send"><Send size={16} /></button>
          </form>
        </div>
      )}
    </div>
  );
}
